/**
 * Admin API client.
 *
 * First slice of the `frontend/admin/admin.js` decomposition (Phase 1.1 of
 * the refactor plan in documentation/code_review_assessment.md). Owns:
 *
 *   - The full URL map (every backend route the admin SPA talks to).
 *   - `fetch()` — auth-aware wrapper around `window.fetch` that surfaces a
 *     friendly error string and triggers an `onUnauthorized` callback on 401.
 *   - `formatErrorDetail()` — translates FastAPI/Pydantic error envelopes
 *     into single-line user-visible strings.
 *
 * Boot contract: the host page must load this script BEFORE `admin.js`. The
 * URL map and fetch wrapper become available as `window.adminAPI`. `admin.js`
 * then calls `adminAPI.configure({...})` once on startup to inject the
 * cross-cutting helpers (`formatLabel`, `onUnauthorized`) that still live
 * in `admin.js` for now — they'll move out in Phase 1.2/1.3.
 *
 * Phase 3.2.a: dropped the IIFE wrapper; module is now ESM (strict by
 * default). Public surface exported as named exports + still attached to
 * `window.adminAPI` for view modules that haven't migrated yet.
 */

export const routes = {
    login: '/auth/login',
    logout: '/auth/logout',
    validate: '/auth/session', // optional: GET, should 200 if valid
    changePassword: '/auth/change-password',
    // Self-service reset: the way out of a forgotten password. Both answer
    // the same whoever asks — see endpoints/auth.py.
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    // Self-service security (own sessions, own sign-ins, own second factor)
    mySessions: '/auth/sessions',
    mySessionsRevokeAll: '/auth/sessions/revoke-all',
    mySignInActivity: (limit = 20) => `/auth/sign-in-activity?limit=${encodeURIComponent(limit)}`,
    mfaBegin: '/auth/mfa/begin',
    mfaConfirm: '/auth/mfa/confirm',
    mfaDisable: '/auth/mfa/disable',
    mfaRecoveryCodes: '/auth/mfa/recovery-codes',
    // Security administration (root / system_admin)
    securityOverview: '/api/admin/security/overview',
    securityLocked: '/api/admin/security/locked',
    securityAttempts: (qs = '') => `/api/admin/security/attempts${qs ? `?${qs}` : ''}`,
    securityAttemptsByIp: (hours = 24) => `/api/admin/security/attempts/by-ip?hours=${encodeURIComponent(hours)}`,
    securityIpBlocks: (qs = '') => `/api/admin/security/ip-blocks${qs ? `?${qs}` : ''}`,
    securityIpBlockRelease: (id) => `/api/admin/security/ip-blocks/${encodeURIComponent(id)}/release`,
    securityIpBlockDelete: (id) => `/api/admin/security/ip-blocks/${encodeURIComponent(id)}`,
    securitySessions: (qs = '') => `/api/admin/security/sessions${qs ? `?${qs}` : ''}`,
    securityRevokeUserSessions: (id) => `/api/admin/security/users/${encodeURIComponent(id)}/revoke-sessions`,
    securityRequirePasswordChange: (id) => `/api/admin/security/users/${encodeURIComponent(id)}/require-password-change`,
    securityResetMfa: (id) => `/api/admin/security/users/${encodeURIComponent(id)}/mfa-reset`,
    securityUsersPosture: '/api/admin/security/users/posture',
    products: '/products/',      // GET list, POST create
    product: id => `/products/${encodeURIComponent(id)}`, // GET/PUT/DELETE
    productDisable: id => `/products/${encodeURIComponent(id)}/disable`, // optional PATCH/POST
    users: '/users/',            // GET list, POST create
    user: id => `/users/${encodeURIComponent(id)}`,         // GET/PUT/DELETE
    userRoles: id => `/users/${encodeURIComponent(id)}/roles`, // GET
    userAssignRole: id => `/users/${encodeURIComponent(id)}/roles`, // POST { name }
    userRevokeRole: (id, name) => `/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(name)}`,
    roles: '/users/roles',
    aps: '/access_points/',      // GET list, POST create
    ap: id => `/access_points/${encodeURIComponent(id)}`, // GET/PUT/DELETE
    apDisable: id => `/access_points/${encodeURIComponent(id)}/disable`, // optional PATCH/POST
    customers: '/customers/',     // GET list, supports ?q,limit,offset
    customerDetail: ({ phone, mac }) => {
      const p = new URLSearchParams();
      if (phone) p.set('phone', phone);
      if (mac) p.set('mac', mac);
      return `/customers/detail?${p.toString()}`;
    },
    adminDistributors: '/api/admin/hotspot/distributors',
    adminDistributor: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}`,
    adminDistributorSales: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/sales`,
    adminDistributorWallet: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/wallet`,
    adminDistributorPayouts: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/payout-requests`,
    adminDistributorTopups: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/topups`,
    adminDistributorManualTopup: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/topups/manual`,
    adminDistributorPin: (id) => `/api/admin/hotspot/distributors/${encodeURIComponent(id)}/pin`,
    adminDistributorAssignAp: '/api/admin/hotspot/distributors/assign-ap',
    adminCampaigns: '/api/admin/campaigns',
    adminCampaign: id => `/api/admin/campaigns/${encodeURIComponent(id)}`,
    adminCampaignRefresh: id => `/api/admin/campaigns/${encodeURIComponent(id)}/refresh-balance`,
    adminCampaignComplete: id => `/api/admin/campaigns/${encodeURIComponent(id)}/complete`,
    adminTechnicians: '/api/ticketing/admin/technicians',
    adminTechnician: id => `/api/ticketing/admin/technicians/${encodeURIComponent(id)}`,
    adminTechnicianStatus: id => `/api/ticketing/admin/technicians/${encodeURIComponent(id)}/status`,
    adminTechnicianOpenTickets: id => `/api/ticketing/admin/technicians/${encodeURIComponent(id)}/open-tickets`,
    adminDailyStatus: '/api/ticketing/admin/technicians/daily-status',
    adminTechnicianOutputTrend: '/api/ticketing/admin/technicians/output-trend',
    adminDailyStatusExport: (day) => {
      const params = new URLSearchParams();
      if (day) params.set('day', day);
      const qs = params.toString();
      return `/api/ticketing/admin/technicians/daily-status/export${qs ? `?${qs}` : ''}`;
    },
    adminTickets: '/api/ticketing/admin/tickets',
    adminTicket: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}`,
    adminTicketActivities: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/activities`,
    adminTicketComments: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/comments`,
    adminTicketComment: (ticketId, commentId) => `/api/ticketing/admin/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(commentId)}`,
    adminTicketCommentRetry: (ticketId, commentId) => `/api/ticketing/admin/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(commentId)}/review/retry`,
    adminTicketCommentOverride: (ticketId, commentId) => `/api/ticketing/admin/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(commentId)}/review/override`,
    adminTicketBoard: '/api/ticketing/admin/boards/technician',
    adminCustomerBoard: '/api/ticketing/admin/boards/customer-care',
    adminCustomerBoardDashboard: '/api/ticketing/admin/boards/customer-care/dashboard',
    adminCustomerBoardParkedSummary: '/api/ticketing/admin/boards/customer-care/parked-summary',
    adminTechnicianQueueSummary: '/api/ticketing/admin/boards/technician/queue-summary',
    adminCustomerCareQueueSummary: '/api/ticketing/admin/boards/customer-care/queue-summary',
    adminTicketTechReview: (id) => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/tech-closure/review`,
    adminTicketTechReviewAiRetry: (id) => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/tech-closure/ai/retry`,
    adminActiveCustomerCareAgents: '/api/ticketing/admin/boards/customer-care/active-agents',
    adminCustomerCareSupervisors: '/api/ticketing/admin/boards/customer-care/supervisors',
    adminTicketBilling: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/billing`,
    adminTicketBillingRefresh: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/billing/refresh`,
    adminTicketBillingUnsuspend: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/billing/unsuspend`,
    adminTicketDuplicateCandidates: (id, query = '', limit = 100) => {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/duplicate-candidates${qs ? `?${qs}` : ''}`;
    },
    adminTicketLinkDuplicate: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/duplicates`,
    adminTicketRequestEquipment: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/request-equipment`,
    adminTicketPark: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/park`,
    adminTicketUnpark: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/unpark`,
    adminSlaPolicies: '/api/ticketing/admin/sla-policies',
    adminSupervisors: '/api/ticketing/admin/supervisors',
    adminTicketPortaCandidates: (id, mode = 'phone', query = '') => {
      const params = new URLSearchParams();
      if (mode) params.set('mode', mode);
      if (query) params.set('query', query);
      const qs = params.toString();
      return `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/porta-candidates${qs ? `?${qs}` : ''}`;
    },
    adminSupportCustomer: (id) => `/api/ticketing/admin/customers/${encodeURIComponent(id)}`,
    adminTicketPortaConnections: (id, portaCustomerId) => {
      const params = new URLSearchParams();
      if (portaCustomerId) params.set('i_customer', portaCustomerId);
      const qs = params.toString();
      return `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/porta-connections${qs ? `?${qs}` : ''}`;
    },
    adminIdentifyCustomer: id => `/api/ticketing/admin/tickets/${encodeURIComponent(id)}/identify`,
    adminPendingMessages: (qs = '') => `/api/ticketing/admin/pending-messages${qs ? `?${qs}` : ''}`,
    adminPendingMessage: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}`,
    adminPendingMessageConvert: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/convert`,
    adminPendingMessageForward: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/forward`,
    adminPendingMessagesDashboard: (qs = '') => `/api/ticketing/admin/pending-messages/dashboard${qs ? `?${qs}` : ''}`,
    adminPendingMessageDuplicates: (qs = '') => `/api/ticketing/admin/pending-messages/duplicates${qs ? `?${qs}` : ''}`,
    adminPendingMessageDuplicatesPurge: '/api/ticketing/admin/pending-messages/duplicates/purge',
    // HR admin endpoints (read-only listings for UI)
    adminHrEmployees: '/api/hr/admin/hr/employees',
    adminHrEmployee: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}`,
    adminHrLinkableUsers: (search = '') => `/api/hr/admin/hr/employees/linkable-users${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    adminHrEmployeeLinkAccount: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/link-account`,
    adminHrEmployeeUnlinkAccount: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/unlink-account`,
    adminHrDepartments: '/api/hr/admin/hr/departments',
    adminHrDepartment: (id) => `/api/hr/admin/hr/departments/${encodeURIComponent(id)}`,
    adminHrPositions: '/api/hr/admin/hr/positions',
    adminHrPosition: (id) => `/api/hr/admin/hr/positions/${encodeURIComponent(id)}`,
    adminHrGrades: '/api/hr/admin/hr/grades',
    adminHrGrade: (id) => `/api/hr/admin/hr/grades/${encodeURIComponent(id)}`,
    adminHrLeaveRequests: '/api/hr/admin/hr/leave_requests',
    adminHrLeaveRequestAnalysis: (id) => `/api/hr/admin/hr/leave_requests/${encodeURIComponent(id)}/analysis`,
    adminHrAttendance: '/api/hr/admin/hr/attendance_records',
    adminHrLoans: '/api/hr/admin/hr/loans',
    adminHrReviews: '/api/hr/admin/hr/performance/reviews',
    adminHrGoals: '/api/hr/admin/hr/performance/goals',
    adminHrGoalCreate: '/api/hr/admin/hr/performance/goals',
    adminHrGoal: (id) => `/api/hr/admin/hr/performance/goals/${encodeURIComponent(id)}`,
    adminHrGoalCheckin: (id) => `/api/hr/admin/hr/performance/goals/${encodeURIComponent(id)}/checkin`,
    adminHrReviewCreate: '/api/hr/admin/hr/performance/reviews',
    adminHrReview: (id) => `/api/hr/admin/hr/performance/reviews/${encodeURIComponent(id)}`,
    adminHrAiScores: '/api/hr/admin/hr/performance/ai-scores',
    adminHrAiScoreHistory: (adminUserId) => `/api/hr/admin/hr/performance/ai-scores/${encodeURIComponent(adminUserId)}`,
    adminHrAiScoreRecompute: '/api/hr/admin/hr/performance/ai-scores/recompute',
    adminHrAiScoreRecomputeYear: '/api/hr/admin/hr/performance/ai-scores/recompute-year',
    adminHrAiScoreRecomputeYearJob: (jobId) => `/api/hr/admin/hr/performance/ai-scores/recompute-year/${encodeURIComponent(jobId)}`,
    adminHrAiScoreYearSummary: '/api/hr/admin/hr/performance/ai-scores/year-summary',
    adminHrApplications: '/api/hr/admin/hr/recruitment/applications',
    adminHrApplication: (id) => `/api/hr/admin/hr/recruitment/applications/${encodeURIComponent(id)}`,
    adminHrApplicationCreate: '/api/hr/admin/hr/recruitment/applications',
    adminHrApplicants: '/api/hr/admin/hr/recruitment/applicants',
    adminHrApplicantCreate: '/api/hr/admin/hr/recruitment/applicants',
    adminHrVacancies: '/api/hr/admin/hr/recruitment/vacancies',
    adminHrOnboarding: '/api/hr/admin/hr/onboarding',
    adminHrOnboardingCreate: '/api/hr/admin/hr/onboarding',
    adminHrOnboardingTaskCreate: '/api/hr/admin/hr/onboarding/tasks',
    adminHrOnboardingTasks: (id) => `/api/hr/admin/hr/onboarding/${encodeURIComponent(id)}/tasks`,
    adminHrOnboardingTemplates: '/api/hr/admin/hr/onboarding/templates',
    adminHrOnboardingTemplateCreate: '/api/hr/admin/hr/onboarding/templates',
    adminHrOnboardingTemplate: (id) => `/api/hr/admin/hr/onboarding/templates/${encodeURIComponent(id)}`,
    adminHrOnboardingTemplateItems: (templateId) => `/api/hr/admin/hr/onboarding/templates/${encodeURIComponent(templateId)}/items`,
    adminHrOnboardingTemplateItem: (id) => `/api/hr/admin/hr/onboarding/template_items/${encodeURIComponent(id)}`,
    adminHrProbation: '/api/hr/admin/hr/probation',
    adminHrProbationCreate: '/api/hr/admin/hr/probation',
    adminHrTrainingSessions: '/api/hr/admin/hr/training/sessions',
    adminHrTrainingSessionCreate: '/api/hr/admin/hr/training/sessions',
    adminHrTrainingBulkEnroll: (id) => `/api/hr/admin/hr/training/sessions/${encodeURIComponent(id)}/enrollments/bulk`,
    adminHrTrainingCourses: '/api/hr/admin/hr/training/courses',
    adminHrTrainingCourseCreate: '/api/hr/admin/hr/training/courses',
    adminHrTrainingEnrollments: '/api/hr/admin/hr/training/enrollments',
    adminHrSkillCreate: '/api/hr/admin/hr/skills',
    adminHrSkillRequirements: '/api/hr/admin/hr/skills/requirements',
    adminHrSkillRequirementCreate: '/api/hr/admin/hr/skills/requirements',
    adminHrSkill: (id) => `/api/hr/admin/hr/skills/${encodeURIComponent(id)}`,
    adminHrSkillRequirement: (id) => `/api/hr/admin/hr/skills/requirements/${encodeURIComponent(id)}`,
    adminHrTrainingEnrollmentCreate: '/api/hr/admin/hr/training/enrollments',
    adminHrTrainingEnrollment: (id) => `/api/hr/admin/hr/training/enrollments/${encodeURIComponent(id)}`,
    adminHrCertifications: '/api/hr/admin/hr/certifications',
    adminHrEmployeeCert: '/api/hr/admin/hr/certifications/employee',
    adminHrEmployeeCertifications: (employeeId) => `/api/hr/admin/hr/employees/${encodeURIComponent(employeeId)}/certifications`,
    adminHrEmployeeCertification: (employeeId, certId) => `/api/hr/admin/hr/employees/${encodeURIComponent(employeeId)}/certifications/${encodeURIComponent(certId)}`,
    adminHrSkills: '/api/hr/admin/hr/skills',
    adminHrEmployeeSkill: '/api/hr/admin/hr/skills/employee',
    adminHrEmployeeSkills: (id) => `/api/hr/admin/hr/skills/employee/${encodeURIComponent(id)}`,
    adminHrOnboardingTaskComplete: (id) => `/api/hr/admin/hr/onboarding/tasks/${encodeURIComponent(id)}/complete`,
    adminHrProbationCheckins: '/api/hr/admin/hr/probation/checkins',
    adminHrLoansCreate: '/api/hr/admin/hr/loans',
    adminHrLoanRepay: (id) => `/api/hr/admin/hr/loans/${encodeURIComponent(id)}/repayments`,
    adminHrLoanApprove: (id) => `/api/hr/admin/hr/loans/${encodeURIComponent(id)}/approvals`,
    adminHrLoan: (id) => `/api/hr/admin/hr/loans/${encodeURIComponent(id)}`,
    adminHrLoanInsight: (id) => `/api/hr/admin/hr/loans/${encodeURIComponent(id)}/insight`,
    adminHrLoanPolicies: '/api/hr/admin/hr/loan-policies',
    adminHrLoanPolicy: (id) => `/api/hr/admin/hr/loan-policies/${encodeURIComponent(id)}`,
    adminHrProfileChangeCreate: '/api/hr/admin/hr/profile_changes',
    adminHrProfileChangeApprove: (id) => `/api/hr/admin/hr/profile_changes/${encodeURIComponent(id)}/approve`,
    adminHrDocuments: '/api/hr/admin/hr/documents',
    adminHrDocumentUpload: '/api/hr/admin/hr/documents/upload',
    adminHrDocumentDownload: (id) => `/api/hr/admin/hr/documents/${encodeURIComponent(id)}/download`,
    adminHrDocument: (id) => `/api/hr/admin/hr/documents/${encodeURIComponent(id)}`,
    adminHrStatusAudits: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/status_audits`,
    adminHrEmployeeQualifications: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/qualifications`,
    adminHrEmployeeQualification: (id, qid) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/qualifications/${encodeURIComponent(qid)}`,
    adminHrReport: '/api/hr/admin/hr/report',
    adminHrReportDownload: (format) => `/api/hr/admin/hr/report/download?format=${encodeURIComponent(format)}`,
    adminHrReportSubscriptions: '/api/hr/admin/hr/report/subscriptions',
    adminHrTaskRun: (task) => `/api/hr/admin/hr/jobs/run?task=${encodeURIComponent(task)}`,
    adminHrCompliance: '/api/hr/admin/hr/compliance',
    adminHrComplianceConfig: '/api/hr/admin/hr/compliance/config',
    adminHrComplianceRecompute: '/api/hr/admin/hr/compliance/recompute',
    adminHrComplianceGaps: (factor, format) => `/api/hr/admin/hr/compliance/gaps?factor=${encodeURIComponent(factor)}${format === 'csv' ? '&format=csv' : ''}`,
    adminHrComplianceWaivers: '/api/hr/admin/hr/compliance/waivers',
    adminHrPositionDocRequirements: (id) => `/api/hr/admin/hr/positions/${encodeURIComponent(id)}/document-requirements`,
    adminHrEmployeeWaivers: (id) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/compliance-waivers`,
    adminHrEmployeeWaiver: (id, wid) => `/api/hr/admin/hr/employees/${encodeURIComponent(id)}/compliance-waivers/${encodeURIComponent(wid)}`,
    adminEssMe: '/api/hr/ess/me',
    adminEssProfile: '/api/hr/ess/me/profile',
    adminEssTraining: '/api/hr/ess/me/training',
    adminEssReviews: '/api/hr/ess/me/reviews',
    adminEssLeave: '/api/hr/ess/me/leave',
    adminEssLeaveAnalysis: (id) => `/api/hr/ess/me/leave/${encodeURIComponent(id)}/analysis`,
    adminEssApprovals: '/api/hr/ess/me/approvals',
    adminEssApprovalDecide: (id) => `/api/hr/ess/me/approvals/${id}/decide`,
    adminEssLoans: '/api/hr/ess/me/loans',
    adminEssPolicies: '/api/hr/ess/me/policies',
    adminEssQuizzes: '/api/hr/ess/me/quizzes',
    adminEssQuizStart: (id) => `/api/hr/ess/me/quizzes/${id}/start`,
    adminEssQuizAnswer: (id) => `/api/hr/ess/me/quizzes/${id}/answer`,
    adminEssQuizSubmit: (id) => `/api/hr/ess/me/quizzes/${id}/submit`,
    adminHrPolicies: '/api/hr/admin/hr/policies',
    adminHrPolicyImport: '/api/hr/admin/hr/policies/import',
    adminHrPolicyImportTemplate: '/api/hr/admin/hr/policies/import/template',
    adminHrPolicy: (id) => `/api/hr/admin/hr/policies/${encodeURIComponent(id)}`,
    adminHrPolicyQuizzes: (id) => `/api/hr/admin/hr/policies/${encodeURIComponent(id)}/quizzes`,
    adminHrPolicyQuizGenerate: (id) => `/api/hr/admin/hr/policies/${encodeURIComponent(id)}/quizzes/generate`,
    adminHrQuiz: (id) => `/api/hr/admin/hr/quizzes/${encodeURIComponent(id)}`,
    adminHrQuizResults: (id) => `/api/hr/admin/hr/quizzes/${encodeURIComponent(id)}/results`,
    adminHrQuizAudience: (id) => `/api/hr/admin/hr/quizzes/${encodeURIComponent(id)}/audience`,
    adminHrQuizLaunch: (id) => `/api/hr/admin/hr/quizzes/${encodeURIComponent(id)}/launch`,
    adminHrQuizCoverage: '/api/hr/admin/hr/quizzes/coverage',
    adminHrQuizBackfill: '/api/hr/admin/hr/quizzes/backfill',
    adminHrGeneralQuizzes: '/api/hr/admin/hr/general-quizzes',
    adminHrGeneralQuiz: (id) => `/api/hr/admin/hr/general-quizzes/${encodeURIComponent(id)}`,
    adminHrGeneralQuizAudience: (id) => `/api/hr/admin/hr/general-quizzes/${encodeURIComponent(id)}/audience`,
    adminHrGeneralQuizBank: (id) => `/api/hr/admin/hr/general-quizzes/${encodeURIComponent(id)}/bank`,
    adminHrGeneralQuizLaunch: (id) => `/api/hr/admin/hr/general-quizzes/${encodeURIComponent(id)}/launch`,
    adminHrGeneralQuizNotifyOverdue: (id) => `/api/hr/admin/hr/general-quizzes/${encodeURIComponent(id)}/notify-overdue`,
    adminHrAnnouncements: '/api/hr/admin/hr/announcements',
    adminHrPublicHolidays: '/api/hr/admin/hr/public-holidays',
    adminHrPublicHoliday: (id) => `/api/hr/admin/hr/public-holidays/${encodeURIComponent(id)}`,
    adminHrLeaveRoster: '/api/hr/admin/hr/leave-roster',
    adminHrQuizCompletion: '/api/hr/admin/hr/quiz-completion',
    adminHrQuizStaffScores: (quarter) => `/api/hr/admin/hr/quiz-completion/staff${quarter ? `?quarter=${encodeURIComponent(quarter)}` : ''}`,
    adminHrQuizStaffReport: (format, quarter) => {
      const p = new URLSearchParams();
      if (format) p.set('format', format);
      if (quarter) p.set('quarter', quarter);
      const qs = p.toString();
      return `/api/hr/admin/hr/quiz-completion/staff/report${qs ? `?${qs}` : ''}`;
    },
    adminHrProfileChanges: '/api/hr/admin/hr/profile_changes',
    adminEmailOutbox: (limit = 100) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return `/api/ticketing/admin/tools/outgoing-email${qs ? `?${qs}` : ''}`;
    },
    adminPendingMessageContactContext: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/contact-context`,
    adminPendingMessageEnquirySuggestions: (query, limit = 8) => {
      const params = new URLSearchParams();
      params.set('query', query);
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return `/api/ticketing/admin/pending-messages/enquiry/suggestions${qs ? `?${qs}` : ''}`;
    },
    adminPendingMessageEnquiry: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/enquiry`,
    adminRegions: '/api/ticketing/admin/regions',
    adminRegion: (id) => `/api/ticketing/admin/regions/${encodeURIComponent(id)}`,
    adminShiftToday: '/api/ticketing/shifts/today',
    adminShiftLogin: '/api/ticketing/shifts/login',
    adminShiftOptions: '/api/ticketing/shifts/options',
    adminShiftActive: '/api/ticketing/shifts/active',
    adminShiftLogout: '/api/ticketing/shifts/logout',
    adminShiftTimeoutClose: '/api/ticketing/shifts/timeout-close',
    adminPendingMessageRecording: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/recording`,
    adminPendingMessageFollowupCandidates: (id, query, limit = 15) => {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/followup-candidates${qs ? `?${qs}` : ''}`;
    },
    crmCustomers: (params = '') => `/api/crm/customers${params ? `?${params}` : ''}`,
    crmCustomer: (id) => `/api/crm/customers/${encodeURIComponent(id)}`,
    crmCustomerRefreshBilling: (id) => `/api/crm/customers/${encodeURIComponent(id)}/refresh-billing`,
    crmCustomerLink: (id) => `/api/crm/customers/${encodeURIComponent(id)}/link`,
    crmCustomerPortaSearch: (params = '') => `/api/crm/customers/porta-search${params ? `?${params}` : ''}`,
    crmCustomerActivities: (id, params = '') => `/api/crm/customers/${encodeURIComponent(id)}/activities${params ? `?${params}` : ''}`,
    crmCustomerTimeline: (id, params = '') => `/api/crm/customers/${encodeURIComponent(id)}/timeline${params ? `?${params}` : ''}`,
    crmCustomerActivity: (id, aid) => `/api/crm/customers/${encodeURIComponent(id)}/activities/${encodeURIComponent(aid)}`,
    crmCustomerFollowUp: (id) => `/api/crm/customers/${encodeURIComponent(id)}/follow-up`,
    crmCustomerReclassify: (id) => `/api/crm/customers/${encodeURIComponent(id)}/reclassify`,
    crmCustomerClose: (id) => `/api/crm/customers/${encodeURIComponent(id)}/close`,
    crmCustomerMap: () => '/api/crm/customers/map',
    crmCustomerMapExport: () => '/api/crm/customers/map/export',
    crmCustomerReport: () => '/api/crm/customers/report',
    crmCustomerReportSegment: (segment, format) => `/api/crm/customers/report/segment?segment=${encodeURIComponent(segment)}${format === 'csv' ? '&format=csv' : ''}`,
    revenueCompliance: (params = '') => `/api/crm/revenue-compliance${params ? `?${params}` : ''}`,
    revenueComplianceRecompute: () => '/api/crm/revenue-compliance/recompute',
    revenueComplianceExport: () => '/api/crm/revenue-compliance/export',
    revenueComplianceCredits: (params = '') => `/api/crm/revenue-compliance/credits${params ? `?${params}` : ''}`,
    revenueComplianceCreditsExport: (params = '') => `/api/crm/revenue-compliance/credits/export${params ? `?${params}` : ''}`,
    revenueComplianceCreditReview: (iXdr) => `/api/crm/revenue-compliance/credits/${encodeURIComponent(iXdr)}/review`,
    crmReconciliation: (params = '') => `/api/crm/reconciliation${params ? `?${params}` : ''}`,
    crmReconciliationPreview: (id) => `/api/crm/reconciliation/${encodeURIComponent(id)}/preview`,
    crmReconciliationApply: (id) => `/api/crm/reconciliation/${encodeURIComponent(id)}/apply`,
    crmReconciliationDismiss: (id) => `/api/crm/reconciliation/${encodeURIComponent(id)}/dismiss`,
    crmReconciliationApplyBatch: '/api/crm/reconciliation/apply-batch',
    crmReconciliationDismissBatch: '/api/crm/reconciliation/dismiss-batch',
    crmReconciliationLink: (id) => `/api/crm/reconciliation/${encodeURIComponent(id)}/link`,
    crmReconciliationMergeInto: (id) => `/api/crm/reconciliation/${encodeURIComponent(id)}/merge-into`,
    crmReconciliationPortaSearch: (params = '') => `/api/crm/reconciliation/porta-search${params ? `?${params}` : ''}`,
    crmRetentionCandidates: (params = '') => `/api/crm/retention/candidates${params ? `?${params}` : ''}`,
    crmRetentionCase: (id) => `/api/crm/retention/${encodeURIComponent(id)}/case`,
    crmRetentionScore: (id) => `/api/crm/retention/${encodeURIComponent(id)}/score`,
    crmRetentionDossier: (id) => `/api/crm/retention/${encodeURIComponent(id)}/dossier`,
    crmRetentionCadence: (id) => `/api/crm/retention/${encodeURIComponent(id)}/cadence`,
    crmRetentionOutcome: (id) => `/api/crm/retention/${encodeURIComponent(id)}/outcome`,
    crmRetentionRemove: (id) => `/api/crm/retention/${encodeURIComponent(id)}/remove`,
    crmRetentionClaim: (id) => `/api/crm/retention/${encodeURIComponent(id)}/claim`,
    crmRetentionRelease: (id) => `/api/crm/retention/${encodeURIComponent(id)}/release`,
    crmRetentionTerminations: () => '/api/crm/retention/terminations',
    crmRetentionTerminate: (id) => `/api/crm/retention/${encodeURIComponent(id)}/terminate`,
    crmRetentionReverseTermination: (id) => `/api/crm/retention/${encodeURIComponent(id)}/reverse-termination`,
    crmRetentionLoadByAgent: () => '/api/crm/retention/load-by-agent',
    crmRetentionChurn: (params = '') => `/api/crm/retention/churn${params ? `?${params}` : ''}`,
    crmRetentionChurnCustomers: (params = '') => `/api/crm/retention/churn/customers${params ? `?${params}` : ''}`,
    crmRetentionReport: (params = '') => `/api/crm/retention/report${params ? `?${params}` : ''}`,
    crmRetentionReportSegment: (segment, format, days) => `/api/crm/retention/report/segment?segment=${encodeURIComponent(segment)}${days ? `&days=${encodeURIComponent(days)}` : ''}${format === 'csv' ? '&format=csv' : ''}`,
    crmRetentionAtRisk: (params = '') => `/api/crm/retention/at-risk${params ? `?${params}` : ''}`,
    crmRetentionTracker: () => '/api/crm/retention/tracker',
    crmProspects: (params = '') => `/api/crm/prospects${params ? `?${params}` : ''}`,
    crmProspect: (id) => `/api/crm/prospects/${encodeURIComponent(id)}`,
    crmProspectsSummary: () => '/api/crm/prospects/summary',
    crmProspectTargets: (params = '') => `/api/crm/prospects/targets${params ? `?${params}` : ''}`,
    crmProspectPool: (params = '') => `/api/crm/prospects/pool${params ? `?${params}` : ''}`,
    crmProspectActivities: (id) => `/api/crm/prospects/${encodeURIComponent(id)}/activities`,
    crmProspectConfirm: (id) => `/api/crm/prospects/${encodeURIComponent(id)}/confirm`,
    crmProspectConvert: (id) => `/api/crm/prospects/${encodeURIComponent(id)}/convert`,
    crmProspectCheckDuplicate: (params = '') => `/api/crm/prospects/check-duplicate${params ? `?${params}` : ''}`,
    crmProspectsReconcilePreview: () => '/api/crm/prospects/reconcile/preview',
    crmProspectsReconcileApply: () => '/api/crm/prospects/reconcile/apply',
    crmProspectOwners: () => '/api/crm/prospects/owners',
    crmProspectSearch: (query, limit = 10) => {
      const params = new URLSearchParams();
      params.set('query', query);
      params.set('limit', String(limit));
      return `/api/crm/prospects/search?${params.toString()}`;
    },
    crmEnquiries: (params = '') => `/api/crm/enquiries${params ? `?${params}` : ''}`,
    // Quotation generator
    crmProspectQuotes: (pid) => `/api/crm/prospects/${encodeURIComponent(pid)}/quotes`,
    crmCustomerQuoteProspect: (customerId) => `/api/crm/customers/${encodeURIComponent(customerId)}/quote-prospect`,
    crmQuote: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}`,
    crmQuoteDraft: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/draft`,
    crmQuoteDraftRender: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/draft/render`,
    crmQuotePublish: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/publish`,
    crmQuoteVersions: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/versions`,
    crmQuoteVersionRender: (qid, n) => `/api/crm/quotes/${encodeURIComponent(qid)}/versions/${encodeURIComponent(n)}/render`,
    crmQuoteVersionPdf: (qid, n) => `/api/crm/quotes/${encodeURIComponent(qid)}/versions/${encodeURIComponent(n)}/pdf`,
    crmQuoteVersionSend: (qid, n) => `/api/crm/quotes/${encodeURIComponent(qid)}/versions/${encodeURIComponent(n)}/send`,
    crmQuoteStatus: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/status`,
    crmQuoteApprovals: (qid) => `/api/crm/quotes/${encodeURIComponent(qid)}/approvals`,
    crmQuoteDecide: (qid, kind) => `/api/crm/quotes/${encodeURIComponent(qid)}/approvals/${encodeURIComponent(kind)}/decide`,
    crmQuoteApprovalsInbox: (params = '') => `/api/crm/quote-approvals${params ? `?${params}` : ''}`,
    invoicingInvoices: (params = '') => `/api/invoicing/invoices${params ? `?${params}` : ''}`,
    invoicingInvoice: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}`,
    invoicingVoid: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/void`,
    invoicingAdjust: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/adjust`,
    // Rebuild an unsent invoice from the customer's current profile + billing (same number).
    invoicingRegenerate: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/regenerate`,
    invoicingSend: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/send`,
    invoicingRender: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/render`,
    invoicingPdf: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/pdf`,
    invoicingCustomerPreferences: (pid) => `/api/invoicing/customers/${encodeURIComponent(pid)}/preferences`,
    invoicingApprovalsInbox: (params = '') => `/api/invoicing/invoice-approvals${params ? `?${params}` : ''}`,
    invoicingCustomerBilling: (pid) => `/api/invoicing/customers/${encodeURIComponent(pid)}/billing`,
    invoicingDecide: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/approvals/decide`,
    invoicingRelease: (id) => `/api/invoicing/invoices/${encodeURIComponent(id)}/release`,
    invoicingReminders: (params = '') => `/api/invoicing/reminders${params ? `?${params}` : ''}`,
    invoicingReminderCreate: () => '/api/invoicing/reminders',
    invoicingReminderGenerate: (id) => `/api/invoicing/reminders/${encodeURIComponent(id)}/generate`,
    invoicingReminderCancel: (id) => `/api/invoicing/reminders/${encodeURIComponent(id)}`,
    invoicingReminderUpdate: (id) => `/api/invoicing/reminders/${encodeURIComponent(id)}`,
    invoicingTaxCustomers: (params = '') => `/api/invoicing/taxation/customers${params ? `?${params}` : ''}`,
    invoicingTaxCustomerDetail: (pid) => `/api/invoicing/taxation/customers/${encodeURIComponent(pid)}`,
    invoicingTaxWithholdings: () => '/api/invoicing/taxation/withholdings',
    invoicingTaxApprove: (id) => `/api/invoicing/taxation/withholdings/${encodeURIComponent(id)}/approve`,
    invoicingTaxReject: (id) => `/api/invoicing/taxation/withholdings/${encodeURIComponent(id)}/reject`,
    invoicingTaxPost: (id) => `/api/invoicing/taxation/withholdings/${encodeURIComponent(id)}/post`,
    invoicingTaxDocument: (id) => `/api/invoicing/taxation/withholdings/${encodeURIComponent(id)}/document`,
    crmQuoteCatalogue: () => `/api/crm/quote-catalogue`,
    crmQuoteScopes: () => `/api/crm/quote-scopes`,
    crmQuoteScope: (id) => `/api/crm/quote-scopes/${encodeURIComponent(id)}`,
    crmQuoteSettings: () => `/api/crm/quote-settings`,
    crmQuoteSettingsLogo: () => `/api/crm/quote-settings/logo`,
    crmInstallations: (params = '') => `/api/crm/installations${params ? `?${params}` : ''}`,
    crmInstallationsReport: (params = '') => `/api/crm/installations/report${params ? `?${params}` : ''}`,
    crmInstallationsDashboard: (params = '') => `/api/crm/installations/dashboard${params ? `?${params}` : ''}`,
    crmInstallationsExport: (params = '') => `/api/crm/installations/export${params ? `?${params}` : ''}`,
    crmInstallation: (id) => `/api/crm/installations/${encodeURIComponent(id)}`,
    crmInstallationValidate: (id) => `/api/crm/installations/${encodeURIComponent(id)}/validate`,
    crmInstallationRevertValidation: (id) => `/api/crm/installations/${encodeURIComponent(id)}/revert-validation`,
    crmInstallationFlag: (id) => `/api/crm/installations/${encodeURIComponent(id)}/flag`,
    crmInstallationPayments: (id) => `/api/crm/installations/${encodeURIComponent(id)}/payments`,
    crmInstallationPole: (id) => `/api/crm/installations/${encodeURIComponent(id)}/pole-delivery`,
    crmInstallationEquipment: (id) => `/api/crm/installations/${encodeURIComponent(id)}/equipment-assign`,
    crmInstallationTicket: (id) => `/api/crm/installations/${encodeURIComponent(id)}/create-ticket`,
    crmInstallationAssignTech: (id) => `/api/crm/installations/${encodeURIComponent(id)}/assign-technician`,
    crmInstallationDidNumber: (id) => `/api/crm/installations/${encodeURIComponent(id)}/did-number`,
    crmInstallationReclose: (id) => `/api/crm/installations/${encodeURIComponent(id)}/reclose`,
    crmInstallationAutoProvision: (id) => `/api/crm/installations/${encodeURIComponent(id)}/auto-provision`,
    crmInstallationRefund: (id) => `/api/crm/installations/${encodeURIComponent(id)}/refund`,
    crmInstallationUnrefund: (id) => `/api/crm/installations/${encodeURIComponent(id)}/unrefund`,
    crmConfigProducts: (params = '') => `/api/crm/config/products${params ? `?${params}` : ''}`,
    crmConfigProduct: (id) => `/api/crm/config/products/${encodeURIComponent(id)}`,
    crmConfigSync: (params = '') => `/api/crm/config/products/sync${params ? `?${params}` : ''}`,
    crmConfigPackageTiers: '/api/crm/config/products/package-tiers',
    crmPromotions: (params = '') => `/api/crm/promotions${params ? `?${params}` : ''}`,
    crmPromotion: (id) => `/api/crm/promotions/${encodeURIComponent(id)}`,
    crmInstallationPromotions: (id) => `/api/crm/installations/${encodeURIComponent(id)}/promotions`,
    crmInstallationPromotion: (id, gid) => `/api/crm/installations/${encodeURIComponent(id)}/promotions/${encodeURIComponent(gid)}`,
    // Inventory (M7)
    inventoryItems: (params = '') => `/api/inventory/items${params ? `?${params}` : ''}`,
    inventoryItemLedger: (id, params = '') => `/api/inventory/items/${encodeURIComponent(id)}/ledger${params ? `?${params}` : ''}`,
    inventoryItem: (id) => `/api/inventory/items/${encodeURIComponent(id)}`,
    inventoryCategories: (params = '') => `/api/inventory/categories${params ? `?${params}` : ''}`,
    inventoryCategory: (id) => `/api/inventory/categories/${encodeURIComponent(id)}`,
    inventoryImportTemplate: () => `/api/inventory/import/template`,
    inventoryImportPreview: () => `/api/inventory/import/preview`,
    inventoryImportCommit: () => `/api/inventory/import/commit`,
    inventoryLocations: (params = '') => `/api/inventory/locations${params ? `?${params}` : ''}`,
    inventoryLocation: (id) => `/api/inventory/locations/${encodeURIComponent(id)}`,
    inventoryDocuments: () => `/api/inventory/documents`,
    inventoryInternalVoucher: () => `/api/inventory/documents/internal-voucher`,
    inventoryDocumentUpload: () => `/api/inventory/documents/upload`,
    inventoryDocumentDownload: (id) => `/api/inventory/documents/${encodeURIComponent(id)}/download`,
    inventoryIssueTargets: (params = '') => `/api/inventory/pickers/issue-targets${params ? `?${params}` : ''}`,
    inventoryTechnicianTargets: () => `/api/inventory/pickers/technicians`,
    inventoryMovements: (params = '') => `/api/inventory/movements${params ? `?${params}` : ''}`,
    inventoryMovementsSummary: (params = '') => `/api/inventory/movements/summary${params ? `?${params}` : ''}`,
    inventoryMovementReceive: () => `/api/inventory/movements/receive`,
    inventoryMovementIssue: () => `/api/inventory/movements/issue`,
    inventoryMovementTransfer: () => `/api/inventory/movements/transfer`,
    inventoryMovementDeploy: () => `/api/inventory/movements/deploy`,
    inventoryMovementDeinstall: () => `/api/inventory/movements/deinstall`,
    inventoryMovementAssign: () => `/api/inventory/movements/assign`,
    inventoryMovementReturn: () => `/api/inventory/movements/return`,
    inventoryMovementAdjust: () => `/api/inventory/movements/adjust`,
    inventoryMovementWriteOff: () => `/api/inventory/movements/write-off`,
    inventoryMovementDispose: () => `/api/inventory/movements/dispose`,
    inventoryApprovals: () => `/api/inventory/approvals`,
    inventoryApprovalsList: (params = '') => `/api/inventory/approvals${params ? `?${params}` : ''}`,
    inventoryApprovalDecide: (id) => `/api/inventory/approvals/${encodeURIComponent(id)}/decide`,
    inventoryAssets: (params = '') => `/api/inventory/assets${params ? `?${params}` : ''}`,
    inventoryAssetsSummary: '/api/inventory/assets/summary',
    inventoryAsset: (id) => `/api/inventory/assets/${encodeURIComponent(id)}`,
    inventoryAlertsLowStock: () => `/api/inventory/alerts/low-stock`,
    inventoryValuation: () => `/api/inventory/valuation`,
    inventoryScan: (params = '') => `/api/inventory/scan${params ? `?${params}` : ''}`,
    inventorySuppliers: () => `/api/inventory/suppliers`,
    inventorySupplier: (id) => `/api/inventory/suppliers/${encodeURIComponent(id)}`,
    inventoryPurchaseOrders: () => `/api/inventory/purchase-orders`,
    inventoryStockRequests: (params = '') => `/api/inventory/stock-requests${params ? `?${params}` : ''}`,
    inventoryStockTakes: () => `/api/inventory/stock-takes`,
    // Infrastructure Ops — admin board (Operations console).
    infraBoardSummary: () => `/api/infra/board/summary`,
    infraWorkOrders: (params = '') => `/api/infra/work-orders${params ? `?${params}` : ''}`,
    infraWorkOrder: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}`,
    infraWorkOrderAssign: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/assign`,
    infraWorkOrderStart: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/start`,
    infraWorkOrderComplete: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/complete`,
    infraWorkOrderBlock: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/block`,
    infraWorkOrderReviewQuality: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/review-quality`,
    infraWorkOrderClose: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/close`,
    infraWorkOrderEscalate: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/escalate`,
    infraWorkOrderPriority: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/priority`,
    infraWorkOrderRequestMaterials: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/request-materials`,
    infraMaterialItems: () => `/api/infra/work-orders/material-items`,
    // Infra-Ops material-request approval gate (supervisor → root), which every
    // work-order material request clears before Inventory sees it.
    infraMaterialRequests: (params = '') => `/api/infra/material-requests${params ? `?${params}` : ''}`,
    infraMaterialRequestApprove: (id) => `/api/infra/material-requests/${encodeURIComponent(id)}/approve`,
    infraMaterialRequestReject: (id) => `/api/infra/material-requests/${encodeURIComponent(id)}/reject`,
    // The restock leg: unused work-order stock going back, each line gated by an
    // Inventory `restock` approval before its receive posts.
    infraMaterialReturns: (params = '') => `/api/infra/material-returns${params ? `?${params}` : ''}`,
    infraMaterialReturnDecide: (id) => `/api/infra/material-returns/${encodeURIComponent(id)}/decide`,
    // Checklist & tasks on a work order
    infraChecklistTemplates: (params = '') => `/api/infra/checklist-templates${params ? `?${params}` : ''}`,
    infraChecklistTemplate: (id) => `/api/infra/checklist-templates/${encodeURIComponent(id)}`,
    infraWorkOrderMaterialRequests: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/material-requests`,
    infraWorkOrderApplyChecklist: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/apply-checklist`,
    infraWorkOrderTasks: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/tasks`,
    infraWorkOrderTask: (id, taskId) => `/api/infra/work-orders/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
    infraWorkOrderTaskStatus: (id, taskId) => `/api/infra/work-orders/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/status`,
    infraWorkOrderTasksReorder: (id) => `/api/infra/work-orders/${encodeURIComponent(id)}/tasks/reorder`,
    // Module-level runtime knobs (GPS accuracy bar the field app enforces).
    infraSettings: () => `/api/infra/settings`,
    infraTechnicians: (params = '') => `/api/infra/technicians${params ? `?${params}` : ''}`,
    infraTechnician: (id) => `/api/infra/technicians/${encodeURIComponent(id)}`,
    infraEligibleUsers: () => `/api/infra/eligible-users`,
    infraTechnicianSkills: (id) => `/api/infra/technicians/${encodeURIComponent(id)}/skills`,
    infraWorkSets: (params = '') => `/api/infra/work-sets${params ? `?${params}` : ''}`,
    infraWorkSet: (id) => `/api/infra/work-sets/${encodeURIComponent(id)}`,
    infraWorkSetStaffing: () => `/api/infra/work-sets/staffing`,
    infraPmCalendar: (params = '') => `/api/infra/pm-calendar${params ? `?${params}` : ''}`,
    infraPmActivity: (params = '') => `/api/infra/pm-activity${params ? `?${params}` : ''}`,
    infraAssetMaintenance: (unitId) => `/api/infra/assets/${encodeURIComponent(unitId)}/maintenance`,
    infraPmSchedules: (params = '') => `/api/infra/pm-schedules${params ? `?${params}` : ''}`,
    infraPmSchedule: (id) => `/api/infra/pm-schedules/${encodeURIComponent(id)}`,
    infraPmEligibleAssets: () => `/api/infra/pm-eligible-assets`,
    infraPmSweepRun: () => `/api/infra/pm-sweep/run`,
    infraWorkGroups: (params = '') => `/api/infra/work-groups${params ? `?${params}` : ''}`,
    infraWorkGroup: (id) => `/api/infra/work-groups/${encodeURIComponent(id)}`,
    infraWorkGroupTeam: (id) => `/api/infra/work-groups/${encodeURIComponent(id)}/team`,
    infraFacilities: (params = '') => `/api/infra/facilities${params ? `?${params}` : ''}`,
    infraFacility: (id) => `/api/infra/facilities/${encodeURIComponent(id)}`,
    // Delete a facility created in error (manager/root; 409 while referenced).
    infraFacilityDelete: (id) => `/api/infra/facilities/${encodeURIComponent(id)}`,
    infraFacilityAssignableAssets: (params = '') => `/api/infra/facilities/assignable-assets${params ? `?${params}` : ''}`,
    infraFacilityAssets: (id) => `/api/infra/facilities/${encodeURIComponent(id)}/assets`,
    infraFacilityAssetUnlink: (id, unitId) => `/api/infra/facilities/${encodeURIComponent(id)}/assets/${encodeURIComponent(unitId)}`,
    infraFacilityDocuments: (id) => `/api/infra/facilities/${encodeURIComponent(id)}/documents`,
    infraFacilityDocumentDownload: (id, docId) => `/api/infra/facilities/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/download`,
    infraFacilitiesImportTemplate: () => '/api/infra/facilities/import/template',
    infraFacilitiesImportPreview: () => '/api/infra/facilities/import/preview',
    infraFacilitiesImportCommit: () => '/api/infra/facilities/import/commit',
    // NOC outages (ticketing) — list/create + hand off to Infra-Ops.
    nocOutages: (params = '') => `/api/ticketing/noc/outages${params ? `?${params}` : ''}`,
    nocOutage: (id) => `/api/ticketing/noc/outages/${encodeURIComponent(id)}`,
    nocOutageHandoff: (id) => `/api/ticketing/noc/outages/${encodeURIComponent(id)}/handoff`,
    adminPendingMessageFollowup: id => `/api/ticketing/admin/pending-messages/${encodeURIComponent(id)}/followup`,
    adminChannelConfigs: '/api/ticketing/channel-settings/configs',
    adminChannelConfig: (channel, mailboxKey) => {
      const params = new URLSearchParams();
      if (mailboxKey && channel === 'email') {
        params.set('mailbox_key', mailboxKey);
      }
      const qs = params.toString();
      return `/api/ticketing/channel-settings/configs/${encodeURIComponent(channel)}${qs ? `?${qs}` : ''}`;
    },
    adminChannelTemplates: ({ channel, event, locale, includeDisabled = true } = {}) => {
      const params = new URLSearchParams();
      if (channel) params.set('channel', channel);
      if (event) params.set('event', event);
      if (locale) params.set('locale', locale);
      if (includeDisabled !== undefined) params.set('include_disabled', includeDisabled ? 'true' : 'false');
      const qs = params.toString();
      return `/api/ticketing/channel-settings/templates${qs ? `?${qs}` : ''}`;
    },
    adminChannelTemplateCreate: '/api/ticketing/channel-settings/templates',
    adminChannelTemplateEnable: (id) => `/api/ticketing/channel-settings/templates/${encodeURIComponent(id)}/enable`,
    adminChannelTemplatePreview: (id) => `/api/ticketing/channel-settings/templates/${encodeURIComponent(id)}/preview`,
    adminChannelTemplateTestSend: (id) => `/api/ticketing/channel-settings/templates/${encodeURIComponent(id)}/test-send`,
    adminWhatsAppCsatPrompts: '/api/ticketing/channel-settings/whatsapp/csat-prompts',
    adminPbxSyncTrunks: '/api/ticketing/channel-settings/pbx/trunks/sync',
    adminShiftTemplates: '/api/hr/shifts',
    adminShiftTemplate: (id) => `/api/hr/shifts/${encodeURIComponent(id)}`,
    // Standing shift HR sets per employee (staff with none are on the default).
    adminHrStaffShifts: (on = '') => `/api/hr/staff-shifts${on ? `?on=${encodeURIComponent(on)}` : ''}`,
    adminHrStaffShift: (employeeId) => `/api/hr/staff-shifts/${encodeURIComponent(employeeId)}`,
    adminHrStaffShiftDetail: (employeeId, on = '') => `/api/hr/staff-shifts/${encodeURIComponent(employeeId)}${on ? `?on=${encodeURIComponent(on)}` : ''}`,
    adminHrStaffShiftHistory: (employeeId) => `/api/hr/staff-shifts/${encodeURIComponent(employeeId)}/history`,
    adminHrStaffShiftAssignment: (employeeId, assignmentId) => `/api/hr/staff-shifts/${encodeURIComponent(employeeId)}/assignments/${encodeURIComponent(assignmentId)}`,
    adminHrAttendanceImportPreview: '/api/hr/admin/hr/attendance/import/preview',
    adminHrAttendanceImportCommit: '/api/hr/admin/hr/attendance/import/commit',
    adminHrAttendanceGrid: (view, date) => `/api/hr/admin/hr/attendance/grid?view=${encodeURIComponent(view)}${date ? `&date=${encodeURIComponent(date)}` : ''}`,
    adminHrAttendanceRecompute: (view, date) => `/api/hr/admin/hr/attendance/recompute?view=${encodeURIComponent(view)}${date ? `&date=${encodeURIComponent(date)}` : ''}`,
    adminSystemHealthSummary: (run) => `/api/admin/system-health/summary${run ? '?run=true' : ''}`,
    adminSystemHealthRun: (check) => `/api/admin/system-health/run${check ? `?check=${encodeURIComponent(check)}` : ''}`,
    adminSystemHealthFollowups: '/api/admin/system-health/followups',
    adminReportsAccessible: '/api/reports/accessible',
    adminReportAccessConfig: '/api/reports/access-config',
    adminReportAccessConfigSet: (key) => `/api/reports/access-config/${encodeURIComponent(key)}`,
    adminSystemHealthJobs: '/api/admin/system-health/jobs',
    adminSystemHealthJobLogs: (name, lines) => `/api/admin/system-health/jobs/${encodeURIComponent(name)}/logs${lines ? `?lines=${encodeURIComponent(lines)}` : ''}`,
    adminAIPrompts: '/api/ticketing/admin/ai-prompts',
    adminAIPromptVersions: (code) => `/api/ticketing/admin/ai-prompts/${encodeURIComponent(code)}/versions`,
    adminAIPromptRollback: (code, version) => `/api/ticketing/admin/ai-prompts/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/rollback`,
    adminAuthmonAps: '/api/admin/authmon/aps',
    adminAuthmonApDetail: (gw) => `/api/admin/authmon/aps/${encodeURIComponent(gw)}`,
    adminAuthmonPurge: '/api/admin/authmon/purge',
    adminGraceSessions: (q, limit = 50, offset = 0) => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (limit) p.set('limit', String(limit));
      if (offset) p.set('offset', String(offset));
      return `/api/admin/hotspot/grace-sessions?${p.toString()}`;
    },
    sessions: (q, limit=50, offset=0) => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (limit) p.set('limit', String(limit));
      if (offset) p.set('offset', String(offset));
      return `/session/hotspot/list?${p.toString()}`;
    },
    sessionTerminate: mac => `/session/hotspot/terminate?mac=${encodeURIComponent(mac)}`,
    adminMobileSessions: (q, limit=50, offset=0) => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (limit) p.set('limit', String(limit));
      if (offset) p.set('offset', String(offset));
      return `/api/admin/mobile/sessions?${p.toString()}`;
    },
    adminMobileRevoke: (keyId) => `/api/admin/mobile/sessions/${encodeURIComponent(keyId)}/revoke`,
    adminMobileCustomers: (q, limit=50, offset=0) => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (limit) p.set('limit', String(limit));
      if (offset) p.set('offset', String(offset));
      return `/api/admin/mobile/customers?${p.toString()}`;
    },
    adminMobileCustomer: (id) => `/api/admin/mobile/customers/${encodeURIComponent(id)}`,
    adminMobilePendingPayments: (q, limit=50, offset=0) => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (limit) p.set('limit', String(limit));
      if (offset) p.set('offset', String(offset));
      return `/api/admin/mobile/payments/pending?${p.toString()}`;
    },
    adminMobilePendingPaymentComplete: (id) => `/api/admin/mobile/payments/${encodeURIComponent(id)}/complete`,
    adminMobilePendingPaymentManualProcessed: (id) => `/api/admin/mobile/payments/${encodeURIComponent(id)}/mark-manual-processed`,
    adminMobilePaymentManualComplete: (id) => `/api/admin/mobile/payments/${encodeURIComponent(id)}/manual-complete`,
    adminMobilePaymentProof: (id) => `/api/admin/mobile/payments/${encodeURIComponent(id)}/proof`,
    adminMobilePaymentCancel: (id) => `/api/admin/mobile/payments/${encodeURIComponent(id)}/cancel`,
    adminMobilePurgeStaleInitiated: () => '/api/admin/mobile/payments/purge-stale-initiated',
  };

  // Injected via configure() at boot. Defaults are no-ops so the API client
  // is usable in isolation (e.g. for tests) without the surrounding admin
  // SPA wired up.
  let formatLabel = (value) => (typeof value === 'string' ? value : '');
  let onUnauthorized = () => {};

  function configure(options) {
    if (!options) return;
    if (typeof options.formatLabel === 'function') {
      formatLabel = options.formatLabel;
    }
    if (typeof options.onUnauthorized === 'function') {
      onUnauthorized = options.onUnauthorized;
    }
  }

  function formatErrorField(loc) {
    if (!Array.isArray(loc) || !loc.length) return '';
    const field = loc
      .map((part) => (typeof part === 'string' || typeof part === 'number') ? String(part).trim() : '')
      .filter((part) => part && !['body', 'query', 'path', 'response'].includes(part.toLowerCase()))
      .pop();
    return field ? formatLabel(field) : '';
  }

  // Anything longer than this is almost certainly a stack trace, an HTML
  // 5xx page, or a stringified blob — none of which belongs in a flash
  // toast. Real validation messages are short.
  const MAX_USER_FACING_DETAIL_LEN = 240;

  // Heuristic guard against leaking internals into a flash toast. Catches
  // HTML 5xx pages, Python tracebacks, raw exception reprs, control-char
  // soup, and anything obviously too long to be a hand-written message.
  function looksLikeInternalLeak(text) {
    if (typeof text !== 'string') return true;
    const trimmed = text.trim();
    if (!trimmed) return true;
    if (trimmed.length > MAX_USER_FACING_DETAIL_LEN) return true;
    // HTML error page (FastAPI debug, Nginx, reverse-proxy 5xx, etc.)
    if (/^<!doctype\b|^<html\b|<\/?(html|body|head|pre|title)\b/i.test(trimmed)) return true;
    // Python traceback markers leaking through `detail`
    if (/Traceback \(most recent call last\)|File "[^"]+", line \d+|^\s*at [A-Za-z_$][\w.]*\s*\(/m.test(trimmed)) return true;
    // SQLAlchemy / driver exception preambles
    if (/^(sqlalchemy\.|psycopg|asyncpg|OperationalError|IntegrityError|ProgrammingError|InternalError)/i.test(trimmed)) return true;
    return false;
  }

  function formatErrorDetail(detail) {
    if (Array.isArray(detail)) {
      const parts = detail
        .map((item) => formatErrorDetail(item))
        .filter(Boolean);
      return parts.join(', ');
    }
    if (detail && typeof detail === 'object') {
      const field = formatErrorField(detail.loc);
      const candidates = [detail.msg, detail.message, detail.error]
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean);
      for (const text of candidates) {
        if (looksLikeInternalLeak(text)) continue;
        return field ? `${field}: ${text}` : text;
      }
      if (typeof detail.code === 'string' && detail.code.trim() && !looksLikeInternalLeak(detail.code)) {
        return formatLabel(detail.code);
      }
      return '';
    }
    if (typeof detail === 'string') {
      return looksLikeInternalLeak(detail) ? '' : formatLabel(detail);
    }
    return '';
  }

  // User-facing fallback when the server response can't be rendered
  // safely. Status-bucketed so the message is at least directional
  // ("you're not allowed" vs. "the server choked") without exposing
  // the underlying response body.
  function genericMessageForStatus(status) {
    if (status === 401) return 'Your session has expired. Please log in again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 404) return 'The requested resource was not found.';
    if (status === 408 || status === 504) return 'The request timed out. Please try again.';
    if (status === 409) return 'This action conflicts with the current state. Refresh and try again.';
    if (status === 413) return 'The submitted data is too large.';
    if (status === 429) return 'Too many requests — please wait a moment and try again.';
    if (status >= 500) return 'Something went wrong on the server. Please try again or contact support.';
    if (status >= 400) return 'The request could not be completed. Please check the inputs and try again.';
    return 'Request failed.';
  }

  async function apiFetch(url, opts = {}) {
    const isFormData = typeof FormData !== 'undefined' && opts && opts.body instanceof FormData;
    const headers = Object.assign(isFormData ? {} : { 'Content-Type': 'application/json' }, opts.headers || {});
    // Auth is carried by the HttpOnly admin_token cookie — no Authorization
    // header needed. credentials:'same-origin' is the modern fetch default
    // but we set it explicitly so the cookie still rides along under any
    // legacy browser policy.
    const res = await fetch(url, Object.assign({}, opts, { headers, credentials: 'same-origin' }));

    // Try to parse body both on success and error
    const raw = await res.text();
    let data = raw;
    try { data = JSON.parse(raw); } catch {}

    if (!res.ok) {
      const detail = data && (data.detail || data.message || data.error);
      const formatted = formatErrorDetail(detail);
      const msg = formatted || genericMessageForStatus(res.status);
      // Always log the raw response so devs can diagnose without forcing
      // the user to copy a stack trace out of a flash toast. `warn` rather
      // than `error` because expected operational failures (401 on bad
      // password, 404 on missing record) shouldn't trip the red error
      // counter — `console.error` is reserved for programming bugs.
      try {
        console.warn('[admin-api]', res.status, url, { status: res.status, body: data, raw });
      } catch {}
      // `skipUnauthorizedHandler` is for calls where a 401 is an expected
      // answer rather than a dead session — the sign-in sequence, where "enter
      // your two-factor code" arrives as a 401. Letting the global handler run
      // there would clear the session and POST /auth/logout mid-flow, throwing
      // away the scoped token the server just issued.
      if (res.status === 401 && !opts.skipUnauthorizedHandler) {
        try { onUnauthorized(); } catch {}
      }
      const err = new Error(msg);
      err.status = res.status; err.body = data; err.raw = raw; throw err;
    }
    return (typeof data === 'string' || data === '') ? {} : data;
  }

  // Like apiFetch, but for endpoints that return a plain-text/HTML body (not
  // JSON) — e.g. the server-rendered HR compliance report. apiFetch coerces any
  // non-JSON body to `{}`, so string responses need this variant.
  async function apiFetchText(url, opts = {}) {
    const res = await fetch(url, Object.assign({}, opts, {
      headers: Object.assign({}, opts.headers || {}),
      credentials: 'same-origin',
    }));
    const raw = await res.text();
    if (!res.ok) {
      let data = raw;
      try { data = JSON.parse(raw); } catch {}
      const detail = data && (data.detail || data.message || data.error);
      const msg = formatErrorDetail(detail) || genericMessageForStatus(res.status);
      try { console.warn('[admin-api]', res.status, url, { status: res.status, body: data, raw }); } catch {}
      if (res.status === 401) { try { onUnauthorized(); } catch {} }
      const err = new Error(msg);
      err.status = res.status; err.body = data; err.raw = raw; throw err;
    }
    return raw;
  }

// Phase 3.2.e: every caller now uses `import` directly. The
// `window.adminAPI` global no longer exists.
export { apiFetch as fetch, apiFetchText as fetchText, configure, formatErrorDetail };
